import AsyncButton from "@components/button/AsyncButton.component";
import { useSourceStreams } from "@stores/data.store";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";

export interface FullResyncOptions {
    selectedStreams: string[] | null;
    dropSchemaFirst: boolean;
}

interface Props {
    sourceId: string;
    show: boolean;
    onClose: () => void;
    onConfirm: (options: FullResyncOptions) => Promise<void>;
}

function FullResyncModal({ sourceId, show, onClose, onConfirm }: Props) {
    const streams = useSourceStreams(sourceId, show);
    const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set());
    const [dropSchemaFirst, setDropSchemaFirst] = useState(false);
    const [confirming, setConfirming] = useState(false);

    // Reset selections when modal opens
    useEffect(() => {
        if (show && streams.data) {
            setSelectedStreams(new Set(streams.data));
            setDropSchemaFirst(false);
        }
    }, [show, streams.data]);

    const hasMultipleStreams = useMemo(() => {
        return streams.data && streams.data.length > 1;
    }, [streams.data]);

    const allSelected = useMemo(() => {
        return streams.data && selectedStreams.size === streams.data.length;
    }, [streams.data, selectedStreams]);

    const noneSelected = selectedStreams.size === 0;

    const toggleStream = (stream: string) => {
        setSelectedStreams(prev => {
            const next = new Set(prev);
            if (next.has(stream)) {
                next.delete(stream);
            } else {
                next.add(stream);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedStreams(new Set());
        } else {
            setSelectedStreams(new Set(streams.data || []));
        }
    };

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            await onConfirm({
                // If all streams selected, pass null to indicate "all"
                selectedStreams: allSelected ? null : Array.from(selectedStreams),
                dropSchemaFirst,
            });
        } finally {
            setConfirming(false);
        }
    };

    const close = () => {
        if (!confirming) {
            onClose();
        }
    };

    return (
        <Modal show={show} onHide={close}>
            <Modal.Header closeButton>
                <Modal.Title>Full Re-Sync</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>This will re-import all data from the source, ignoring any previous sync state.</p>

                {streams.isLoading && (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" /> Loading streams...
                    </div>
                )}

                {hasMultipleStreams && (
                    <>
                        <Form.Label className="fw-bold">Select streams to re-sync:</Form.Label>
                        <div className="mb-2">
                            <Form.Check
                                type="checkbox"
                                id="select-all-streams"
                                label="Select All"
                                checked={allSelected}
                                onChange={toggleAll}
                            />
                        </div>
                        <div className="border rounded p-2 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {streams.data?.map(stream => (
                                <Form.Check
                                    key={stream}
                                    type="checkbox"
                                    id={`stream-${stream}`}
                                    label={stream}
                                    checked={selectedStreams.has(stream)}
                                    onChange={() => toggleStream(stream)}
                                    className="ms-3"
                                />
                            ))}
                        </div>
                    </>
                )}

                <Form.Check
                    type="checkbox"
                    id="drop-schema-first"
                    label="Wipe existing data before sync"
                    checked={dropSchemaFirst}
                    onChange={(e) => setDropSchemaFirst(e.target.checked)}
                    className="mb-2"
                />

                {dropSchemaFirst && (
                    <Alert variant="warning" className="mb-0">
                        <i className="mdi mdi-alert me-1"></i>
                        {allSelected || !hasMultipleStreams
                            ? "This will delete all existing data in this source before syncing."
                            : `This will delete existing data for the selected stream(s) before syncing.`
                        }
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={close} disabled={confirming}>
                    Cancel
                </Button>
                <AsyncButton
                    icon="mdi mdi-reload"
                    variant="primary"
                    disabled={noneSelected}
                    onClick={handleConfirm}
                    text="Re-Sync"
                    loading={confirming}
                />
            </Modal.Footer>
        </Modal>
    );
}

export default FullResyncModal;
